/**
 * What White-Label → Support shows, as pure functions over the page's GET
 * view and the editor's draft — no React, so the node harness can check it
 * (tests/unit/support_board/support_board_model.test.mjs).
 *
 * The RULE lives on the server (backend/modules/support_board): the nearest
 * choice up the tree decides, "off" stops the walk, no platform default. This
 * file only words what that rule already decided, and reads a pasted board
 * link exactly as `board_choice.parse_board_link` does so the field can say
 * "Board 5011223344 ✓" before anything is saved — the server still decides.
 */

export type Mode = 'inherit' | 'board' | 'off';

export interface Board {
  board_id: string;
  board_name: string | null;
  board_url: string | null;
  source_tenant_id: string;
  source_tenant_name: string;
}

export interface OrgRef { tenant_id: string; tenant_name: string }

export interface OwnChoiceBelow extends OrgRef {
  mode: 'board' | 'off';
  board_name: string | null;
  board_id: string | null;
  board_url: string | null;
}

export interface SupportView {
  tenant_name: string;
  mode: Mode;
  own: Board | null;
  inherited: Board | null;
  inherited_off_by: string | null;
  effective: Board | null;
  parent_name: string | null;
  is_platform_root: boolean;
  organizations_under: number;
  uses_this_board: OrgRef[];
  own_choice_below: OwnChoiceBelow[];
  monday: MondayView;
  delivery: { test_available: boolean; last: LastDelivery | null };
}

/** Who is connected and how — the page never sees a token. */
export interface MondayConnection {
  auth_kind: 'oauth' | 'token';
  account_name: string | null;
  account_slug: string | null;
  user_name: string | null;
  user_email: string | null;
  connected_at: string | null;
}

export interface MondayView {
  oauth_available: boolean;
  /** This organization's own connection. */
  connection: MondayConnection | null;
  /** Inheriting: the connection of the organization that owns the board. */
  owner_connection: MondayConnection | null;
  owner_tenant_name: string | null;
  /** Its own board: what the last check found (missing = null: never checked). */
  board_setup: { title: string | null; checked_at: string | null; missing: string[] | null } | null;
}

export type DeliveryStatus = 'queued' | 'sending' | 'delivered' | 'failed';

export interface LastDelivery {
  status: DeliveryStatus;
  is_test: boolean;
  created_at: string | null;
  delivered_at: string | null;
  attempts: number;
  next_attempt_at: string | null;
  error_code: string | null;
  failing_since: string | null;
  monday_item_id: string | null;
}

/** The connection that delivers this organization's reports, if any. */
export function deliveringConnection(view: SupportView, draft: Draft): MondayConnection | null {
  return draft.mode === 'board' ? view.monday.connection : view.monday.owner_connection;
}

/** How the last delivery reads on the page. */
export type DeliveryLine =
  | { kind: 'none' }
  | { kind: 'delivered'; at: string | null; isTest: boolean }
  | { kind: 'sending' }
  | { kind: 'retrying'; since: string | null; next: string | null; code: string }
  | { kind: 'failed'; code: string };

export function deliveryLine(last: LastDelivery | null): DeliveryLine {
  if (!last) return { kind: 'none' };
  if (last.status === 'delivered') return { kind: 'delivered', at: last.delivered_at, isTest: last.is_test };
  if (last.status === 'failed') return { kind: 'failed', code: last.error_code || 'internal' };
  if (last.error_code) {
    return { kind: 'retrying', since: last.failing_since, next: last.next_attempt_at, code: last.error_code };
  }
  return { kind: 'sending' };
}

/** The words of a board's default name, in the editor's language. */
export interface BoardNameWords {
  withBrand: (brand: string, org: string) => string;
  noBrand: (org: string) => string;
  test: (name: string) => string;
}

type Translate = (key: string, vars?: Record<string, string>) => string;

export const boardNameWords = (t: Translate): BoardNameWords => ({
  withBrand: (brand, org) => t('whiteLabel.supportBoard.monday.defaultName', { brand, org }),
  noBrand: org => t('whiteLabel.supportBoard.monday.defaultNameNoBrand', { org }),
  test: name => t('whiteLabel.supportBoard.monday.defaultNameTest', { name }),
});

/** The default name for a board CorpBrain creates: the organization's own
 *  brand, never another's. */
export function defaultBoardName(words: BoardNameWords, brandName: string, organization: string,
                                 test = false): string {
  const brand = brandName.trim();
  const base = brand ? words.withBrand(brand, organization) : words.noBrand(organization);
  return test ? words.test(base) : base;
}

export interface Draft { mode: Mode; board_link: string; board_name: string }

export const draftOf = (view: SupportView): Draft => ({
  mode: view.mode,
  board_link: view.own?.board_url ?? '',
  board_name: view.own?.board_name ?? '',
});

/** Whether the draft differs from what is saved, as the server would see it. */
export function isDirty(view: SupportView, draft: Draft): boolean {
  const saved = draftOf(view);
  if (draft.mode !== saved.mode) return true;
  if (draft.mode !== 'board') return false;
  return draft.board_link.trim() !== saved.board_link || draft.board_name.trim() !== saved.board_name;
}

// ── The board link, read as the server reads it ─────────────────────────

export type LinkCheck =
  | { state: 'empty' }
  | { state: 'ok'; boardId: string; account: string; url: string }
  | { state: 'error'; code: 'board_link_not_monday' | 'board_link_no_board' };

const BOARD_PATH = /^\/boards\/(\d{1,20})(?:\/.*)?$/;
const ACCOUNT = /^[a-z0-9][a-z0-9-]{0,62}$/;
const NOT_ACCOUNTS = new Set(['www', 'view', 'auth', 'api']);

/** Mirrors `board_choice.parse_board_link`: https://<account>.monday.com/boards/<id>[/…]. */
export function checkBoardLink(raw: string): LinkCheck {
  const text = raw.trim();
  if (!text) return { state: 'empty' };
  let url: URL;
  try {
    url = new URL(text.includes('://') ? text : `https://${text}`);
  } catch {
    return { state: 'error', code: 'board_link_not_monday' };
  }
  const host = url.hostname.toLowerCase();
  if (url.protocol !== 'https:' || !host.endsWith('.monday.com')) {
    return { state: 'error', code: 'board_link_not_monday' };
  }
  const account = host.slice(0, -'.monday.com'.length);
  if (!ACCOUNT.test(account) || NOT_ACCOUNTS.has(account)) {
    return { state: 'error', code: 'board_link_not_monday' };
  }
  const match = BOARD_PATH.exec(url.pathname);
  if (!match) return { state: 'error', code: 'board_link_no_board' };
  return { state: 'ok', boardId: match[1], account, url: `https://${account}.monday.com/boards/${match[1]}` };
}

// ── "What happens" ──────────────────────────────────────────────────────

/** A board as the page shows it: its name, else its number. */
export interface BoardChip { name: string | null; id: string; url: string | null }

export type Summary =
  /** Reports also go to this board; `setOn` names the organization above that chose it.
   *  `notYet`: the organization that owns the board has not connected monday,
   *  so reports are kept (up to a day) until it does — the page must not say
   *  they already go there. */
  | { kind: 'board'; board: BoardChip; setOn: string | null; notYet: boolean }
  /** "Its own board" is chosen but no valid link is in the field yet. */
  | { kind: 'pending' }
  /** PanLuma only, and why. */
  | { kind: 'none'; reason: 'off' | 'off_above' | 'nothing_above' | 'top_level' | 'platform_root'; offBy?: string };

/** A top-level organization has nothing above it to follow. */
export const isTopLevel = (view: SupportView): boolean => !view.parent_name;

/** What happens to a report opened in this organization, if the draft were saved. */
export function summarize(view: SupportView, draft: Draft): Summary {
  if (view.is_platform_root) return { kind: 'none', reason: 'platform_root' };
  if (draft.mode === 'off') return { kind: 'none', reason: 'off' };
  if (draft.mode === 'board') {
    const link = checkBoardLink(draft.board_link);
    if (link.state !== 'ok') return { kind: 'pending' };
    return {
      kind: 'board', setOn: null, notYet: !view.monday.connection,
      board: { name: draft.board_name.trim() || null, id: link.boardId, url: link.url },
    };
  }
  const inherited = view.inherited;
  if (inherited) {
    return {
      kind: 'board',
      setOn: inherited.source_tenant_name,
      notYet: !view.monday.owner_connection,
      board: { name: inherited.board_name, id: inherited.board_id, url: inherited.board_url },
    };
  }
  if (view.inherited_off_by) return { kind: 'none', reason: 'off_above', offBy: view.inherited_off_by };
  return { kind: 'none', reason: isTopLevel(view) ? 'top_level' : 'nothing_above' };
}

// ── The cards ───────────────────────────────────────────────────────────

/**
 * The choices offered. A top-level organization has no "Same as …": with
 * nothing above, following IS "not sent", so it is offered as that card
 * (saved as no choice of its own, unless "off" is what is already stored).
 */
export function cardsFor(view: SupportView): { follow: boolean; offValue: Mode } {
  if (!isTopLevel(view)) return { follow: true, offValue: 'off' };
  return { follow: false, offValue: view.mode === 'off' ? 'off' : 'inherit' };
}

// ── Organizations under this one ────────────────────────────────────────

export type BelowRow =
  | { tenant_id: string; tenant_name: string; kind: 'follows' }
  | { tenant_id: string; tenant_name: string; kind: 'own'; board: BoardChip }
  | { tenant_id: string; tenant_name: string; kind: 'off' };

/**
 * The organizations under this one that either use THIS organization's board
 * or made a choice of their own, by name; `others` counts the rest, which
 * follow the choice of the organization above them.
 */
export function belowRows(view: SupportView, collator?: Intl.Collator): { rows: BelowRow[]; others: number } {
  const rows: BelowRow[] = [
    ...(view.own ? view.uses_this_board : []).map(o => ({ ...pick(o), kind: 'follows' as const })),
    ...view.own_choice_below.map((o): BelowRow => (o.mode === 'off'
      ? { ...pick(o), kind: 'off' }
      : { ...pick(o), kind: 'own', board: { name: o.board_name, id: o.board_id ?? '', url: o.board_url } })),
  ];
  const compare = collator ? collator.compare : (a: string, b: string) => a.localeCompare(b);
  rows.sort((a, b) => compare(a.tenant_name, b.tenant_name));
  return { rows, others: Math.max(0, view.organizations_under - rows.length) };
}

const pick = (o: OrgRef) => ({ tenant_id: o.tenant_id, tenant_name: o.tenant_name });
